import { ReactNode, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWebSocket } from "@/providers/WebSocketProvider";
import { PosQueryKeys } from "@/lib/pos/query";
import type { WSMessage } from "@/hooks/use-unified-websocket";

interface OrderUpdatePayload {
  orderId: number;
  orderNumber: string;
  status: string;
  previousStatus?: string;
  locationId?: number;
  userId?: string;
}

interface NewOrderPayload {
  orderId: number;
  orderNumber: string;
  status: string;
  locationId: number;
  userId?: string;
  totalUsd: string;
  totalSech: string;
}

interface UsePosWebSocketOptions {
  channels?: string[];
  enabled?: boolean;
}

const POS_MESSAGE_TYPES = [
  "order_update",
  "new_order",
  "connected",
  "auth_success",
  "subscribed",
];

/**
 * Backward-compatible POS WebSocket hook.
 * Uses the unified WebSocket under the hood.
 */
export function usePosWebSocket(options?: UsePosWebSocketOptions) {
  const { isConnected, subscribe, unsubscribe, addMessageListener, sendMessage } =
    useWebSocket();
  const queryClient = useQueryClient();
  const [lastMessage, setLastMessage] = useState<WSMessage | null>(null);

  const { channels, enabled = true } = options || {};

  // Subscribe to channels
  useEffect(() => {
    if (enabled && channels && channels.length > 0) {
      subscribe(channels);
      return () => {
        unsubscribe(channels);
      };
    }
  }, [enabled, channels?.join(","), subscribe, unsubscribe]);

  // Listen for POS-relevant messages and handle cache invalidation
  useEffect(() => {
    const removeListener = addMessageListener((message: WSMessage) => {
      if (!POS_MESSAGE_TYPES.includes(message.type)) return;

      setLastMessage(message);

      switch (message.type) {
        case "order_update": {
          const payload = message.payload as OrderUpdatePayload;
          queryClient.invalidateQueries({ queryKey: PosQueryKeys.orders });
          if (payload.userId) {
            queryClient.invalidateQueries({
              queryKey: [...PosQueryKeys.ordersByUser(payload.userId)],
            });
          }
          break;
        }
        case "new_order": {
          const payload = message.payload as NewOrderPayload;
          queryClient.invalidateQueries({ queryKey: PosQueryKeys.orders });
          if (payload.userId) {
            queryClient.invalidateQueries({
              queryKey: [...PosQueryKeys.ordersByUser(payload.userId)],
            });
          }
          break;
        }
        default:
          break;
      }
    });

    return removeListener;
  }, [queryClient, addMessageListener]);

  return {
    isConnected,
    lastMessage,
    subscribe,
    unsubscribe,
    sendMessage,
  };
}

/**
 * No-op passthrough provider for backward compatibility.
 * The unified WebSocketProvider at the app root handles the connection.
 */
export function PosWebSocketProvider({ children }: { children: ReactNode }) {
  return children;
}
